/**
 * Scan configuration shared by every surface that can launch the AI Pentest
 * Agent: the Agent Scanner page's config bar, and "Send to AI Pentest Agent"
 * on an engagement.
 *
 * The defaults and the request body live here rather than in each caller
 * because they are behaviour, not layout. `unlimitedRequests` meaning "no
 * request ceiling, bounded by time instead" is a contract with the
 * orchestrator, and it has already gone wrong once, when a time budget was
 * posted from a screen that had no control behind it and every scan silently
 * stopped at 60 minutes.
 */

export const AI_MODELS = [
  { value: 'kimi-k2', label: 'Kimi K2 Thinking' },
  { value: 'kimi-k2.5', label: 'Kimi K2.5' },
  { value: 'sonnet', label: 'Claude Sonnet 4.6' },
  { value: 'haiku', label: 'Claude Haiku 4.5' },
  { value: 'ollama', label: 'Local (Ollama)' },
];

export const DEFAULT_SCAN_CONFIG = {
  mode: 'guided',
  aiModel: 'kimi-k2',
  budgetRequests: 50000,
  budgetTime: 60,            // minutes — applies when a request ceiling is set
  budgetHours: 4,            // hours — applies instead when requests are unlimited
  budgetTokens: 0,           // 0 = no token ceiling
  unlimitedRequests: false,
  allowDestructive: false,   // off unless asked for: it writes to the target
  // Which of the engagement's scope items to test. null until the scope
  // preview has loaded; the launch screens then fill it with every item id and
  // the operator unticks what they do not want. An explicit list rather than
  // "all" is deliberate — an engagement usually holds several environments,
  // and a scope item added between the confirmation screen and the launch
  // should not join the scan without being seen.
  scopeItemIds: null,
  // A session the tester established themselves, supplied before the scan
  // starts. The route for a login the agent cannot complete on its own — MFA,
  // SSO, a captcha — and supplied here rather than at the mid-scan prompt so
  // recon runs authenticated instead of mapping a login page.
  preAuthKind: 'cookie',   // 'cookie' | 'token'
  preAuthSecret: '',
  // Ceiling on outbound requests per minute. 0 is unthrottled, which is right
  // for a lab and wrong for anything behind a bot manager: a scan that goes as
  // fast as the target answers is the one that gets flagged and refused, and a
  // refused scanner measures the WAF rather than the application.
  requestsPerMinute: 0,
  // Asking for .env, *.bak and friends by name is the half of discovery a bot
  // manager recognises on sight. On by default — it finds real things, and most
  // targets have nothing in front of them.
  probeSensitiveFiles: true,
  // Send Chrome's user agent and header set instead of python-httpx's four.
  browserFingerprint: false,
  // 2.6% of findings across 25 measured scans, and the largest single source of
  // requests. On by default; the first thing to turn off against a monitored
  // target, where the volume is what gets the scan refused.
  runNuclei: true,
};

/** Turn form state into the body POST /agent-scanner/start expects. */
export function buildScanPayload(engagementId, c) {
  return {
    engagementId,
    mode: c.mode,
    aiModel: c.aiModel,
    // 0 requests means no request ceiling; the time limit binds instead.
    budgetRequests: c.unlimitedRequests ? 0 : parseInt(c.budgetRequests, 10),
    budgetTimeMin: c.unlimitedRequests
      ? Math.max(1, Math.round(parseFloat(c.budgetHours) * 60))
      : parseInt(c.budgetTime, 10),
    budgetTokens: parseInt(c.budgetTokens, 10) || 0,
    allowDestructive: c.allowDestructive === true,
    // Omitted rather than null when nothing was chosen: the route reads an
    // absent field as the whole engagement scope, and sending null would mean
    // the same thing by accident instead of by intent.
    ...(Array.isArray(c.scopeItemIds) ? { scopeItemIds: c.scopeItemIds } : {}),
    // Omitted entirely when blank, so an empty box never reaches the database
    // as an empty credential.
    ...((c.preAuthSecret || '').trim()
      ? { preAuth: { kind: c.preAuthKind || 'cookie', secret: c.preAuthSecret.trim() } }
      : {}),
    requestsPerMinute: parseInt(c.requestsPerMinute, 10) || 0,
    probeSensitiveFiles: c.probeSensitiveFiles !== false,
    browserFingerprint: c.browserFingerprint === true,
    runNuclei: c.runNuclei !== false,
  };
}

/** One line describing what the budget will actually stop on. */
export function describeBudget(c) {
  const parts = [];
  parts.push(c.unlimitedRequests
    ? `${c.budgetHours || 0}h`
    : `${Number(c.budgetRequests || 0).toLocaleString()} requests / ${c.budgetTime}min`);
  if (parseInt(c.budgetTokens, 10) > 0) {
    parts.push(`${Number(c.budgetTokens).toLocaleString()} tokens`);
  }
  return parts.join(' · ');
}
